import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";
import { getApiErrorMessage } from "src/types/api";
import axiosInstance from "src/store/features/auth/axiosCSRF";
// 타입 정의 개선
export interface iKeyResidueDistanceParams {
    group_name: string;
    library_name: string;
    file_name: string;
    receptor_chain: string;
    ligand_chain: string;
}

export interface iKeyResidueDistanceResponse {
    [key: string]: any;
}

export interface iKeyResidueDistanceState {
    response: iKeyResidueDistanceResponse | null;
    isLoading: boolean;
    error: string | null;
}

// 초기 상태
const initialState: iKeyResidueDistanceState = {
    response: null,
    isLoading: false,
    error: null
};

// AsyncThunk 개선
export const getKeyResidueDistanceAPI = createAsyncThunk<
    iKeyResidueDistanceResponse,
    iKeyResidueDistanceParams,
    {
        rejectValue: string;
    }
>(
    "keyResidueDistance/getKeyResidueDistance",
    async (params, { rejectWithValue }) => {
        try {
            const djangoEndpoint = getDjangoEndpoint();
            const pathParam = '/tools/key-residue-distance/';
            
            const response = await axiosInstance.post(
                `${djangoEndpoint}${pathParam}`,
                {
                    group_name: params.group_name,
                    library_name: params.library_name,
                    file_name: params.file_name,
                    receptor_chain: params.receptor_chain,
                    ligand_chain: params.ligand_chain
                },
                {
                    withCredentials: true,
                }
            );

            return response.data;
        } catch (error) {
            const errorMessage = getApiErrorMessage(error, 'Unknown error occurred');

            return rejectWithValue(errorMessage);
        }
    }
);

// 슬라이스 정의
export const keyResidueDistanceSlice = createSlice({
    name: "keyResidueDistance",
    initialState,
    reducers: {
        // 완전 초기화
        resetKeyResidueDistance: () => initialState,
        // 에러만 클리어
        clearError: (state) => {
            state.error = null;
        },
        
        // 응답만 클리어 (로딩 상태는 유지)
        clearResponse: (state) => {
            state.response = null;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Pending
            .addCase(getKeyResidueDistanceAPI.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            // Fulfilled
            .addCase(getKeyResidueDistanceAPI.fulfilled, (state, action: PayloadAction<iKeyResidueDistanceResponse>) => {
                state.response = action.payload;
                state.isLoading = false;
                state.error = null;
            })
            // Rejected
            .addCase(getKeyResidueDistanceAPI.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Request failed';
                state.response = null;
            });
    }
});

// 액션 내보내기
export const { 
    resetKeyResidueDistance, 
    clearError, 
    clearResponse 
} = keyResidueDistanceSlice.actions;

// 셀렉터 함수들 (옵션)
export const selectKeyResidueDistance = (state: { keyResidueDistance: iKeyResidueDistanceState }) => state.keyResidueDistance;
export const selectKeyResidueDistanceResponse = (state: { keyResidueDistance: iKeyResidueDistanceState }) => state.keyResidueDistance.response;
export const selectKeyResidueDistanceLoading = (state: { keyResidueDistance: iKeyResidueDistanceState }) => state.keyResidueDistance.isLoading;
export const selectKeyResidueDistanceError = (state: { keyResidueDistance: iKeyResidueDistanceState }) => state.keyResidueDistance.error;

// 리듀서 내보내기
export default keyResidueDistanceSlice.reducer;